import SupplierOrderPurchaseInvoicesSection from '../modules/orders/sections/SupplierOrderPurchaseInvoicesSection';
import SupplierOrderPurchaseCostsSection from '../modules/orders/sections/SupplierOrderPurchaseCostsSection';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tabs,
  Timeline,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DollarOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ShopOutlined,
  TruckOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import { getDisplayOrderNumber } from '../types/order';
import { usePageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import { ORDER_CURRENCIES } from '../modules/orders/currencies';
import {
  SUPPLIER_ORDER_PROGRESS_STATUSES,
  SUPPLIER_ORDER_STATUSES,
  SupplierOrderStatus,
} from '../modules/orders/supplierOrderStatuses';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import { FactoryOptionDto, SupplierOptionDto, UserOptionDto } from '../dto/reference';
import {
  SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES,
  SupplierOrderStatusHistoryDto,
  supplierOrderAmountRule,
  supplierOrderNumberRule,
} from '../dto/supplierOrder';
import { fetchAccountManagerOptions, fetchFactoryOptions, fetchSupplierOptions } from '../modules/reference-data/api/referenceDataApi';
import {
  createSupplierOrderDocument,
  deleteSupplierOrderDocument,
  downloadSupplierOrderDocumentFile,
  downloadSupplierOrderPdf,
  fetchSupplierOrderDocuments,
  getSupplierOrderDisplayNumber,
  SupplierOrderDocumentDto,
  updateSupplierOrder,
} from '../modules/orders/api/supplierOrdersApi';
import SupplierOrderDeliveryTransportsSection from '../modules/orders/sections/SupplierOrderDeliveryTransportsSection';
import SupplierOrderPZDocumentsSection from '../modules/orders/sections/SupplierOrderPZDocumentsSection';
import { useSupplierOrderDetail } from '../modules/orders/queries/useSupplierOrderDetail';
import {
  buildSupplierOrderUpdateData,
  getSupplierOrderFieldNames,
  getSupplierOrderInitialValues,
  SupplierOrderEditableField,
} from '../modules/orders/supplierOrderDetailEditUtils';
import {
  DETAIL_META_GRID_STYLE,
  getOrderTypeBadgeStyle,
  getOrderTypeHeaderStyle,
  getStatusBadgeStyle,
  INFO_GRID_STYLE,
  OrderWorkflowRail,
  PAGE_SHELL_STYLE,
  renderInfoItem,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import OrderDetailSectionCard from '../modules/orders/components/OrderDetailSectionCard';
import OrderInvoiceCoverageSection from '../modules/orders/sections/OrderInvoiceCoverageSection';
import { InvoiceCoverageTag } from '../modules/invoices/components/InvoiceCoverageSummary';
import { SupplierOrderItemsSection } from '../modules/orders/sections/SupplierOrderItemsSection';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { useFeedback } from '../utils/feedback';
import { incompleteValuationWarning, readIncompleteValuationIssues } from '../modules/orders/stockValuationSaveWarning';
import { getApiErrorMessage } from '../utils/apiError';
import { getSupplierOrderErrorMessage, isBlockingSupplierOrderError } from '../modules/orders/supplierOrderErrorMessages';
// Shared detail-page theme (status badge inks, financial strip): the classes
// are order-/supplier-neutral under .order-detail-page, see OrderDetail.css.
import './OrderDetail.css';

const { Text } = Typography;

const SUPPLIER_ORDERS_LIST_PATH = '/app/orders?orderFlow=supplier';
/**
 * Supplier-order detail page. It reuses the customer-order layout primitives,
 * but copy and visible fields stay supplier-side: supplier, factory, purchase
 * documents and landed costs. Every ZD is a stock purchase.
 */
const SupplierOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['orders', 'common', 'tasks']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [editForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [supplierDocumentForm] = Form.useForm();

  const { supplierOrder, loading, refresh } = useSupplierOrderDetail(id);
  // Hoisted so every handler below (edit/status/link/document actions) can
  // name the order in its toast without re-deriving it — mirrors OrderDetail's
  // `orderDisplayNumber`. Guarded by `?` since it's read before the not-found check.
  const displayOrderNumber = supplierOrder ? getSupplierOrderDisplayNumber(supplierOrder) : '';

  usePageHeaderTitleOverride(
    supplierOrder
      ? t('orders:pageHeader.supplier', { number: getDisplayOrderNumber(supplierOrder) })
      : null,
  );

  const [editingField, setEditingField] = useState<SupplierOrderEditableField | null>(null);
  const [savingField, setSavingField] = useState<SupplierOrderEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [factories, setFactories] = useState<FactoryOptionDto[]>([]);
  const [accountManagers, setAccountManagers] = useState<UserOptionDto[]>([]);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [documentLanguage, setDocumentLanguage] = useState<'en' | 'pl'>('pl');
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const [supplierDocuments, setSupplierDocuments] = useState<SupplierOrderDocumentDto[]>([]);
  const [supplierDocumentsLoading, setSupplierDocumentsLoading] = useState(false);
  const [supplierDocumentModalVisible, setSupplierDocumentModalVisible] = useState(false);
  const [supplierDocumentSubmitting, setSupplierDocumentSubmitting] = useState(false);
  const [supplierDocumentDownloadingId, setSupplierDocumentDownloadingId] = useState<string | null>(null);

  const canManage = authService.canManageSupplierOrders();
  const canManagePurchaseInvoices = authService.canManageInvoiceImports();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewSupplierPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  const canDownloadSupplierOrderPdf = authService.canViewSupplierOrderPurchasePdf();
  const canEditSupplierPurchasePrices = authService.canEditSupplierOrderPurchasePrices();
  const canViewSupplierOrderFinance = authService.canViewSupplierOrderFinance();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance();
  const useManualNumberingValue = Form.useWatch('useManualNumbering', editForm);

  const renderExchangeRateToPLN = () => {
    if (!supplierOrder?.exchangeRateToPLN) {
      return supplierOrder?.currency === 'PLN'
        ? <Text type="secondary">{t('orders:supplierOrders.detail.headerRateNotApplicable')}</Text>
        : '-';
    }

    const meta = [
      supplierOrder.exchangeRateToPLNEffectiveDate
        ? `${t('orders:supplierOrders.detail.exchangeRateEffectiveDate')}: ${formatDate(supplierOrder.exchangeRateToPLNEffectiveDate)}`
        : null,
      supplierOrder.exchangeRateToPLNSource || null,
      supplierOrder.exchangeRateToPLNTableNumber
        ? `${t('orders:supplierOrders.detail.exchangeRateTable')}: ${supplierOrder.exchangeRateToPLNTableNumber}`
        : null,
    ].filter(Boolean);

    return (
      <div style={{ textAlign: 'right' }}>
        <strong style={{ color: '#0f172a' }}>{supplierOrder.exchangeRateToPLN}</strong>
        {meta.length > 0 && (
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
            {meta.join(' · ')}
          </div>
        )}
      </div>
    );
  };

  // Fetches the supplier-order PDF as a blob and triggers a browser download,
  // honoring the EN/PL document-language toggle.
  const handleDownloadDocument = async () => {
    if (!id || !supplierOrder || !canDownloadSupplierOrderPdf) return;

    const conversionChoice = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionChoice) return;

    setDocumentDownloading(true);
    try {
      const orderBlob = await downloadSupplierOrderPdf(
        id,
        documentLanguage,
        conversionChoice === 'none' ? undefined : conversionChoice,
      );
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = documentLanguage === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, displayOrderNumber)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('orders:orderDetail.orderDocumentDownloaded', { orderNumber: displayOrderNumber }));
    } catch (error) {
      feedback.error(t('orders:orderDetail.orderDocumentDownloadFailed', { orderNumber: displayOrderNumber }));
    } finally {
      setDocumentDownloading(false);
    }
  };

  const loadSupplierDocuments = async () => {
    if (!id || !canViewPurchaseDocuments) return;

    setSupplierDocumentsLoading(true);
    try {
      const rows = await fetchSupplierOrderDocuments(id);
      setSupplierDocuments(rows);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentsFetchFailed'));
    } finally {
      setSupplierDocumentsLoading(false);
    }
  };

  const openSupplierDocumentModal = () => {
    supplierDocumentForm.resetFields();
    supplierDocumentForm.setFieldsValue({
      documentType: 'SUPPLIER_INVOICE',
      currency: supplierOrder?.currency || 'PLN',
      document: [],
    });
    setSupplierDocumentModalVisible(true);
  };

  const handleCreateSupplierDocument = async () => {
    if (!id) return;

    let values: any = null;
    try {
      values = await supplierDocumentForm.validateFields();
      setSupplierDocumentSubmitting(true);
      const document = await createSupplierOrderDocument(id, values);
      feedback.success(t('orders:supplierOrders.detail.documentAdded', {
        number: document.number || document.fileName || '',
      }));
      setSupplierDocumentModalVisible(false);
      supplierDocumentForm.resetFields();
      await loadSupplierDocuments();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentAddFailed', undefined, {
        type: values?.documentType ? t(`orders:supplierOrders.documentTypes.${values.documentType}`) : '',
      }));
    } finally {
      setSupplierDocumentSubmitting(false);
    }
  };

  const handleDownloadSupplierDocument = async (record: SupplierOrderDocumentDto) => {
    setSupplierDocumentDownloadingId(record.id);
    try {
      const blob = await downloadSupplierOrderDocumentFile(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = record.fileName || `${record.number || record.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentDownloadFailed', undefined, {
        number: record.number || record.fileName || '',
      }));
    } finally {
      setSupplierDocumentDownloadingId(null);
    }
  };

  const handleDeleteSupplierDocument = (record: SupplierOrderDocumentDto) => {
    modal.confirm({
      title: t('orders:supplierOrders.detail.deleteDocumentConfirmTitle'),
      content: record.number || record.fileName,
      okText: t('common:delete'),
      okButtonProps: { danger: true },
      cancelText: t('common:cancel'),
      onOk: async () => {
        await deleteSupplierOrderDocument(record.id);
        feedback.success(t('orders:supplierOrders.detail.documentDeleted', {
          number: record.number || record.fileName || '',
        }));
        await loadSupplierDocuments();
      },
    });
  };

  useEffect(() => {
    if (!canManage) return;

    (async () => {
      try {
        const [supplierRows, factoryRows, accountManagerRows] = await Promise.all([
          fetchSupplierOptions(),
          fetchFactoryOptions(),
          fetchAccountManagerOptions('supplierOrderDetail.accountManagers'),
        ]);
        setSuppliers(supplierRows);
        setFactories(factoryRows);
        setAccountManagers(accountManagerRows);
      } catch {
        // Non-critical — selects fall back to the current value.
      }
    })();
  }, [canManage]);

  useEffect(() => {
    void loadSupplierDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewPurchaseDocuments]);

  const startEditing = (field: SupplierOrderEditableField) => {
    if (!supplierOrder) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getSupplierOrderInitialValues(supplierOrder, field));
  };

  const cancelEditing = (field: SupplierOrderEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: SupplierOrderEditableField) => {
    if (!id || !supplierOrder) return;
    if (field === 'currency' && !canEditSupplierPurchasePrices) return;
    if (field === 'landedCosts' && !canEditSupplierOrderFinance) return;

    try {
      const values = editForm.getFieldsValue(true);
      // recursive: true descends into Form.List entries (landedCosts items);
      // a bare ['landedCosts'] path would skip the per-item amount rules.
      await editForm.validateFields(getSupplierOrderFieldNames(field, values), { recursive: true });
      setSavingField(field);
      const updateData = buildSupplierOrderUpdateData(supplierOrder, field, values);

      const saved = await updateSupplierOrder(id, updateData, { modal, t });
      if (!saved) return;
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      const valuationIssues = readIncompleteValuationIssues(saved);
      if (valuationIssues) feedback.warning(incompleteValuationWarning(valuationIssues, t), { sticky: true });
      cancelEditing(field);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setSavingField(null);
    }
  };

  const saveOrderNumber = async () => {
    if (!id || !supplierOrder) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getSupplierOrderFieldNames('manualNumbering', values), { recursive: true });
      setSavingField('manualNumbering');
      await updateSupplierOrder(id, buildSupplierOrderUpdateData(supplierOrder, 'manualNumbering', values));
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      setNumberModalOpen(false);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!supplierOrder) return {};
    const init = getSupplierOrderInitialValues(supplierOrder, 'manualNumbering');
    if (!supplierOrder.useManualNumbering) {
      init.manualOrderNumber = getSupplierOrderDisplayNumber(supplierOrder);
    }
    return init;
  };

  const openStatusModal = () => {
    if (!supplierOrder) return;
    const [firstAllowedStatus] = supplierOrder.allowedTransitions || [];
    statusForm.setFieldsValue({ status: firstAllowedStatus, statusNotes: '' });
    setStatusModalVisible(true);
  };

  const handleChangeStatus = async (values: any) => {
    if (!id || !supplierOrder) return;

    setStatusSaving(true);
    try {
      await updateSupplierOrder(
        id,
        {
          ...buildSupplierOrderUpdateData(supplierOrder, 'status', { status: values.status }),
          statusNotes: values.statusNotes?.trim() || null,
        }
      );
      feedback.success(t('orders:supplierOrders.detail.statusUpdateSuccess', {
        orderNumber: displayOrderNumber,
        status: t(`orders:supplierOrders.status.${values.status}`),
      }));
      setStatusModalVisible(false);
      statusForm.resetFields();
      await refresh();
    } catch (error: any) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.statusUpdateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setStatusSaving(false);
    }
  };

  // Editable field for fields the supplier-order backend supports.
  const renderEditableField = (
    field: SupplierOrderEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode
  ) => {
    if (!canManage || (field === 'invoice' && !canManagePurchaseInvoices)) return value;

    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
          {renderComponent()}
          <Space wrap>
            <Button
              icon={<SaveOutlined />}
              size="small"
              type="primary"
              loading={savingField === field}
              onClick={() => saveField(field)}
            >
              {t('common:save')}
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={() => cancelEditing(field)}
              disabled={savingField === field}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>{value}</div>
        <Tooltip title={t('orders:orderDetail.edit')}>
          <Button
            aria-label={t('orders:orderDetail.edit')}
            icon={<EditOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={() => startEditing(field)}
            disabled={Boolean(editingField && editingField !== field)}
            style={{ flex: '0 0 auto', marginTop: -3, color: '#475569' }}
          />
        </Tooltip>
      </div>
    );
  };

  const statusHistoryEntries = useMemo<SupplierOrderStatusHistoryDto[]>(() => {
    if (!supplierOrder) return [];
    return Array.isArray(supplierOrder.statusHistory) && supplierOrder.statusHistory.length > 0
      ? supplierOrder.statusHistory
      : [{
        id: 'current',
        supplierOrderId: supplierOrder.id,
        status: supplierOrder.status,
        changedAt: supplierOrder.orderDate,
        notes: undefined,
      }];
  }, [supplierOrder]);

  const landedCostCategoryOptions = useMemo(
    () => SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES.map((cat) => ({
      value: cat,
      label: t(`orders:supplierOrders.landedCostCategory.${cat}`),
    })),
    [t]
  );

  if (loading && (!supplierOrder || supplierOrder.id !== id)) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!supplierOrder) {
    return (
      <div style={{ paddingBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(SUPPLIER_ORDERS_LIST_PATH)}
          style={{ marginBottom: 16, borderRadius: 999, fontWeight: 600 }}
        >
          {t('orders:supplierOrders.detail.backToList')}
        </Button>
        <Card style={SECTION_CARD_STYLE} bodyStyle={SECTION_CARD_BODY_STYLE}>
          {t('orders:supplierOrders.detail.notFound')}
        </Card>
      </div>
    );
  }

  const statusBadgeStyle = getStatusBadgeStyle(supplierOrder.status);
  const isFinancialCompletenessPending = supplierOrder.financialCompleteness?.status === 'PENDING';
  const purchasePricesIncomplete = Boolean(
    canViewSupplierPurchasePrices
      && supplierOrder.financialCompleteness?.missing?.includes('SUPPLIER_PURCHASE_PRICES')
  );
  const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.name, searchText: s.searchText }));
  const factoryOptions = factories.map((f) => ({ value: f.id, label: f.name }));
  const accountManagerOptions = accountManagers.map((manager) => ({
    value: manager.id,
    label: manager.name + (manager.email ? ` (${manager.email})` : ''),
  }));
  const supplierLandedCostPLN = Number(supplierOrder.landedCostAmountPLN || 0);
  const supplierOrderLineCurrencies = Array.from(new Set(
    (supplierOrder.items || [])
      .map((item) => item.currency || supplierOrder.currency || 'PLN')
      .filter(Boolean)
  )).sort();
  const supplierOrderLineCurrenciesLabel = supplierOrderLineCurrencies.length > 0
    ? supplierOrderLineCurrencies.join(', ')
    : '-';
  const allowedStatusTransitions = supplierOrder.allowedTransitions || [];
  const currentProgressStatus = supplierOrder.status as SupplierOrderStatus;
  const supplierAccentColor = '#1d4ed8';
  const supplierAccentBackground = '#eff6ff';
  const isSupplierWorkflowCancelled = supplierOrder.status === 'CANCELLED';
  const isSupplierWorkflowHeld = supplierOrder.status === 'ON_HOLD';
  const supplierWorkflowSteps = SUPPLIER_ORDER_PROGRESS_STATUSES.map((status) => ({
    key: status,
    label: t(`orders:supplierOrders.status.${status}`),
  }));
  const supplierWorkflowCurrentLabel = t(
    isSupplierWorkflowCancelled
      ? 'orders:supplierOrders.detail.cancelledWorkflow'
      : isSupplierWorkflowHeld
        ? 'orders:supplierOrders.detail.heldWorkflow'
        : 'orders:supplierOrders.detail.currentStatus'
  );

  const landedCostsDisplay = (
    <div style={{ display: 'grid', gap: 8, width: '100%' }}>
      {!supplierOrder.landedCosts || supplierOrder.landedCosts.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('orders:supplierOrders.detail.noLandedCosts')}
        </Text>
      ) : (
        supplierOrder.landedCosts.map((item, idx) => (
          <div key={item.id || idx}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 14 }}>
              <div>
                <span>{t(`orders:supplierOrders.landedCostCategory.${item.category}`)}</span>
                {item.note && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{item.note}</Text>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <strong style={{ color: '#0f172a' }}>
                  {formatCurrency(item.originalAmount ?? item.amount, item.originalCurrency || supplierOrder.currency)}
                </strong>
                {item.originalCurrency && item.originalCurrency !== 'PLN' && item.amountPLN != null && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatCurrency(item.amountPLN, 'PLN')}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      <Divider style={{ margin: '2px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 15, fontWeight: 700 }}>
        <span>{t('orders:supplierOrders.detail.landedCostAmount')}</span>
        <strong style={{ color: '#0f172a' }}>{formatCurrency(supplierLandedCostPLN, 'PLN')}</strong>
      </div>
    </div>
  );

  const landedCostsForm = () => (
    <Form form={editForm} layout="vertical" style={{ width: '100%' }}>
      <Form.List name="landedCosts">
        {(fields, { add, remove }) => (
          <div style={{ display: 'grid', gap: 8 }}>
            {fields.map((field) => (
              <Row key={field.key} gutter={[8, 0]} align="top" style={{ flexWrap: 'nowrap' }}>
                <Col flex="180px">
                  <Form.Item
                    name={[field.name, 'category']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCategory') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select options={landedCostCategoryOptions} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col flex="120px">
                  <Form.Item
                    name={[field.name, 'amount']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostAmount') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ ...supplierOrderAmountRule(t), required: true }]}
                  >
                    {/* No min/max clamp: an out-of-range entry must stay visible and
                        fail validation instead of being silently coerced on blur. */}
                    <InputNumber
                      precision={2}
                      style={{ width: '100%' }}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col flex="96px">
                  <Form.Item
                    name={[field.name, 'currency']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCurrency') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      options={ORDER_CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
                    />
                  </Form.Item>
                </Col>
                <Col flex="auto">
                  <Form.Item
                    name={[field.name, 'note']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostNote') : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Input maxLength={500} placeholder={t('orders:supplierOrders.form.landedCostNote')} />
                  </Form.Item>
                </Col>
                <Col flex="32px">
                  <Form.Item
                    label={fields.indexOf(field) === 0 ? ' ' : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ category: 'CUSTOMS_DUTY', amount: 0, currency: supplierOrder.currency || 'PLN', note: '' })}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              {t('orders:supplierOrders.form.addLandedCost')}
            </Button>
          </div>
        )}
      </Form.List>
    </Form>
  );

  const overviewMeta = [
    {
      icon: <ShopOutlined />,
      label: t('orders:supplierOrders.form.supplier'),
      value: supplierOrder.supplier?.name || '-',
    },
    {
      icon: <HomeOutlined />,
      label: t('orders:supplierOrders.form.factory'),
      value: supplierOrder.factory?.name || '-',
    },
    {
      icon: <CalendarOutlined />,
      label: t('orders:supplierOrders.form.orderDate'),
      value: supplierOrder.orderDate ? formatDate(supplierOrder.orderDate) : '-',
    },
    {
      icon: <TruckOutlined />,
      label: t('orders:supplierOrders.form.expectedDelivery'),
      value: supplierOrder.expectedDelivery ? formatDate(supplierOrder.expectedDelivery) : '-',
    },
  ];

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={PAGE_SHELL_STYLE} className="order-detail-page">
        {/* Overview header — clone of OrderDetail's. */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={getOrderTypeHeaderStyle(supplierAccentColor)}
        >
          <div style={{ flex: '1 1 100%', minWidth: 0 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(SUPPLIER_ORDERS_LIST_PATH)}
              style={{ marginBottom: 12, borderRadius: 999, fontWeight: 600 }}
            >
              {t('orders:supplierOrders.detail.backToList')}
            </Button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={getOrderTypeBadgeStyle(supplierAccentColor, supplierAccentBackground)}>
                <ShopOutlined />
                {t('orders:supplierOrders.detail.title')}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                {t('orders:orderDetail.title')}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
	              <div style={{ fontSize: 'clamp(1.45rem, 2.8vw, 2.3rem)', lineHeight: 1.05, fontWeight: 800, letterSpacing: 0, color: '#0f172a', overflowWrap: 'anywhere' }}>
	                {displayOrderNumber}
	              </div>
              {supplierOrder.commerciallyFrozenAt && (
                <Tooltip title={t('orders:supplierOrders.detail.frozenOrderAlert')}>
                  {/* WP11: „Zafakturowane częściowo (x %)” / „w całości”; the plain tag only when coverage is unknown. */}
                  {supplierOrder.invoiceCoverage && supplierOrder.invoiceCoverage.state !== 'NONE'
                    ? <span><InvoiceCoverageTag coverage={supplierOrder.invoiceCoverage} /></span>
                    : <Tag>{t('invoices:workflow.frozenOrder')}</Tag>}
                </Tooltip>
              )}
              {canManage && (
                <Tooltip title={t('orders:orderDetail.editNumber')}>
                  <Button
                    aria-label={t('orders:orderDetail.editNumber')}
                    icon={<EditOutlined />}
                    type="text"
                    size="small"
                    shape="circle"
                    onClick={() => setNumberModalOpen(true)}
                    style={{ color: '#475569' }}
                  />
                </Tooltip>
              )}
              <Modal
                open={numberModalOpen}
                title={t('orders:orderDetail.editNumber')}
                onCancel={() => setNumberModalOpen(false)}
                onOk={saveOrderNumber}
                okText={t('common:save')}
                cancelText={t('common:cancel')}
                confirmLoading={savingField === 'manualNumbering'}
                destroyOnClose
              >
                <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}>
                  <Form.Item
                    name="useManualNumbering"
                    valuePropName="checked"
                    label={t('orders:orderDetail.useManualNumbering')}
                  >
                    <Switch />
                  </Form.Item>
                  {numberFormUseManual && (
                    <Form.Item
                      name="manualOrderNumber"
                      label={t('orders:supplierOrders.form.manualOrderNumber')}
                      rules={[{
                        required: true,
                        whitespace: true,
                        message: t('orders:modal.validation.manualNumberRequired'),
                      }, supplierOrderNumberRule(t)]}
                    >
                      <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
                    </Form.Item>
                  )}
                </Form>
              </Modal>
              {supplierOrder.useManualNumbering && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '5px 10px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 12,
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                }}>
                  {t('orders:orderDetail.manual')}
                </span>
              )}
            </div>

            {!!supplierOrder.withdrawnInvoiceReceipts?.length && (
              <Alert type="warning" showIcon data-testid="supplier-order-withdrawn-invoice" style={{ marginBottom: 12 }}
                message={t('orders:supplierOrders.detail.withdrawnInvoiceReceipts', { numbers: supplierOrder.withdrawnInvoiceReceipts.join(', ') })} />
            )}
            {supplierOrder.commerciallyFrozenAt && (
              // WP1 part 2: an invoiced ZD stays editable; say once what a
              // change after the invoice means (confirmation, money stays).
              <Alert
                type="info"
                showIcon
                message={t('orders:supplierOrders.detail.frozenOrderAlert')}
                style={{ marginBottom: 12 }}
              />
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={statusBadgeStyle}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: 'currentColor' }} />
                {t(`orders:supplierOrders.status.${supplierOrder.status}`)}
              </span>
              {isFinancialCompletenessPending && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                  }}
                >
                  {t('orders:supplierOrders.detail.financialCompletenessPending')}
                </span>
              )}
              {canViewSupplierPurchasePrices && supplierOrder.currency && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#ffffff',
                    border: '1px solid #dbe7f3',
                    color: '#334155',
                  }}
                >
                  {t('orders:supplierOrders.detail.summaryCurrencyValue', { currency: supplierOrder.currency })}
                </span>
              )}
              {canViewSupplierPurchasePrices && supplierOrderLineCurrencies.length > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#eef6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1d4ed8',
                  }}
                >
                  {t('orders:supplierOrders.detail.purchaseLineCurrenciesValue', {
                    currencies: supplierOrderLineCurrenciesLabel,
                  })}
                </span>
              )}
              {canManage && (
                <Button
                  type="primary"
                  onClick={openStatusModal}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.updateStatus')}
                </Button>
              )}
              {canDownloadSupplierOrderPdf && (
                <div
                  style={{
	                    display: 'inline-flex',
	                    alignItems: 'center',
	                    flexWrap: 'wrap',
	                    gap: 8,
	                    padding: '6px 8px',
	                    borderRadius: 999,
	                    background: '#ffffff',
	                    border: '1px solid #dbe7f3',
	                    maxWidth: '100%',
	                  }}
	                >
                  <Segmented
                    size="small"
                    value={documentLanguage}
                    onChange={(value) => setDocumentLanguage(value as 'en' | 'pl')}
                    options={[
                      { label: 'EN', value: 'en' },
                      { label: 'PL', value: 'pl' },
                    ]}
                  />
	                  <Button
	                    icon={<FilePdfOutlined />}
	                    onClick={handleDownloadDocument}
	                    loading={documentDownloading}
	                    style={{ borderRadius: 999, fontWeight: 600, maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}
	                  >
	                    {t('orders:supplierOrders.detail.downloadSupplierOrderDocument')}
	                  </Button>
                </div>
              )}
            </div>

            <div style={DETAIL_META_GRID_STYLE}>
              {overviewMeta.map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div style={{ fontSize: 15, color: '#2563eb', marginTop: 1 }}>{item.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <OrderWorkflowRail
          title={t('orders:supplierOrders.detail.workflow')}
          steps={supplierWorkflowSteps}
          currentKey={currentProgressStatus}
          currentLabel={supplierWorkflowCurrentLabel}
          cancelled={isSupplierWorkflowCancelled}
          cancelledText={t('orders:supplierOrders.detail.cancelledWorkflow')}
          held={isSupplierWorkflowHeld}
          heldText={t('orders:supplierOrders.detail.heldWorkflow')}
        />

        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          size="large"
          style={{ maxWidth: '100%', minWidth: 0 }}
          tabBarStyle={{ marginBottom: 24, fontWeight: 600, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
          items={[
            // ───────────────── Tab 1: Informacje ─────────────────
            {
              key: '1',
              label: t('orders:orderDetail.orderInfo'),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {/* Items card: primary operational surface — the one section
                      that stays expanded (owner 2026-09-14). */}
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
                    <SupplierOrderItemsSection
                      supplierOrder={supplierOrder}
                      canEdit={canManage}
                      canViewSupplierPurchasePrices={canViewSupplierPurchasePrices}
                      purchasePricesIncomplete={purchasePricesIncomplete}
                      onSaved={refresh}
                    />
                  </motion.div>

                  {/* WP11: invoiced / remaining quantity per line (quantities only). */}
                  <OrderInvoiceCoverageSection coverage={supplierOrder.invoiceCoverage} />

                  {/* Card: Informacje */}
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
                    <OrderDetailSectionCard
                      title={t('orders:orderDetail.title')}
                      forceRender
                      style={{ marginBottom: 16 }}
                    >
                        <div style={INFO_GRID_STYLE}>
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.summaryCurrency'),
                            supplierOrder.currency || '-'
                          )}
                          {renderInfoItem(t('orders:orderDetail.createdBy'), supplierOrder.createdBy?.name || '-')}
                          {renderInfoItem(
                            t('orders:orderDetail.accountManager'),
                            renderEditableField('accountManagerId', supplierOrder.accountManager?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="accountManagerId" style={{ margin: 0 }}>
                                  <Select
                                    style={{ width: 260 }}
                                    placeholder={t('orders:modal.selectAccountManager')}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={accountManagerOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.orderDate'),
                            renderEditableField('orderDate', supplierOrder.orderDate ? formatDate(supplierOrder.orderDate) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="orderDate" style={{ margin: 0 }} rules={[{ required: true }]}>
                                  <DatePicker />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.expectedDelivery'),
                            renderEditableField('expectedDelivery', supplierOrder.expectedDelivery ? formatDate(supplierOrder.expectedDelivery) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="expectedDelivery" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.actualDelivery'),
                            renderEditableField('actualDelivery', supplierOrder.actualDelivery ? formatDate(supplierOrder.actualDelivery) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="actualDelivery" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.estimatedCompletionDate'),
                            renderEditableField('estimatedCompletionDate', supplierOrder.estimatedCompletionDate ? formatDate(supplierOrder.estimatedCompletionDate) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="estimatedCompletionDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.description'),
                            renderEditableField('description', supplierOrder.description || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="description" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.notes'),
                            renderEditableField('notes', supplierOrder.notes || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="notes" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                        </div>
                      </OrderDetailSectionCard>
                    </motion.div>

                    {/* Card: Produkcja i logistyka */}
                    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                      <OrderDetailSectionCard
                        title={t('orders:orderDetail.productionLogistics')}
                        forceRender
                        style={{ marginBottom: 16 }}
                      >
                        <div style={INFO_GRID_STYLE}>
                          {renderInfoItem(
                            t('orders:orderDetail.useManualNumbering'),
                            renderEditableField(
                              'manualNumbering',
                              supplierOrder.useManualNumbering ? t('common:yes') : t('common:no'),
                              () => (
                                <Form form={editForm} style={{ display: 'inline-block' }}>
                                  <Space wrap>
                                    <Form.Item name="useManualNumbering" valuePropName="checked" style={{ margin: 0 }}>
                                      <Switch />
                                    </Form.Item>
                                    {useManualNumberingValue && (
                                      <Form.Item
                                        name="manualOrderNumber"
                                        style={{ margin: 0 }}
                                        rules={[{
                                          required: true,
                                          whitespace: true,
                                          message: t('orders:modal.validation.manualNumberRequired'),
                                        }, supplierOrderNumberRule(t)]}
                                      >
                                        <Input
                                          maxLength={64}
                                          placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')}
                                          style={{ width: 220 }}
                                        />
                                      </Form.Item>
                                    )}
                                  </Space>
                                </Form>
                              )
                            ),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.supplier'),
                            renderEditableField('supplierId', supplierOrder.supplier?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="supplierId" style={{ margin: 0 }} rules={[{ required: true }]}>
                                  <Select
                                    style={{ width: 260 }}
                                    showSearch
                                    optionFilterProp="searchText"
                                    options={supplierOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.producer'),
                            renderEditableField('producer', supplierOrder.producer || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="producer" style={{ margin: 0 }}>
                                  <Input style={{ width: 260 }} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:supplierOrders.form.factory'),
                            renderEditableField('factoryId', supplierOrder.factory?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="factoryId" style={{ margin: 0 }}>
                                  <Select
                                    style={{ width: 260 }}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={factoryOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.factoryLocation'),
                            renderEditableField('factoryLocation', supplierOrder.factoryLocation || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="factoryLocation" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.deliveryLocation'),
                            renderEditableField('deliveryLocation', supplierOrder.deliveryLocation || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="deliveryLocation" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                        </div>
                      </OrderDetailSectionCard>
                    </motion.div>

                    {/* Card: Dokumenty dostawcy */}
                    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
                      <OrderDetailSectionCard
                        title={t('orders:supplierOrders.detail.supplierDocuments')}
                        extra={canManagePurchaseInvoices ? <Button icon={<UploadOutlined />} onClick={openSupplierDocumentModal}>{t('orders:supplierOrders.detail.addSupplierDocument')}</Button> : null}
                        forceRender
                        style={{ marginBottom: 16 }}
                      >
                        <div style={INFO_GRID_STYLE}>
                          {renderInfoItem(
                            t('orders:orderDetail.proformaNumber'),
                            renderEditableField('proforma', supplierOrder.proformaNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="proformaNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.proformaDate'),
                            editingField === 'proforma' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="proformaDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.proformaDate ? formatDate(supplierOrder.proformaDate) : '-'
                            )
                          )}
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.supplierInvoiceNumber'),
                            renderEditableField('invoice', supplierOrder.invoiceNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="invoiceNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.supplierInvoiceDate'),
                            editingField === 'invoice' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="invoiceDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.invoiceDate ? formatDate(supplierOrder.invoiceDate) : '-'
                            )
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.waybillNumber'),
                            renderEditableField('waybill', supplierOrder.waybillNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="waybillNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.waybillDate'),
                            editingField === 'waybill' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="waybillDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.waybillDate ? formatDate(supplierOrder.waybillDate) : '-'
                            )
                          )}
                          {renderInfoItem(
                            t('orders:supplierOrders.form.supplierReference'),
                            renderEditableField('supplierReference', supplierOrder.supplierReference || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="supplierReference" style={{ margin: 0 }}>
                                  <Input />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.clientOrderNumber'),
                            renderEditableField('clientOrderNumber', supplierOrder.clientOrderNumber || '-', () => (
                              <Form form={editForm}>
                                <Form.Item name="clientOrderNumber" style={{ margin: 0 }}>
                                  <Input style={{ width: 220 }} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                        </div>
                        {canViewPurchaseDocuments && canViewSupplierOrderFinance && (
                          <Table
                            rowKey="id"
                            size="middle"
                            loading={supplierDocumentsLoading}
                            pagination={{ pageSize: 10, hideOnSinglePage: true }}
                            dataSource={supplierDocuments}
                            columns={[
                              {
                                title: t('orders:supplierOrders.detail.documentType'),
                                dataIndex: 'documentType',
                                key: 'documentType',
                                render: (value: string) => t(`orders:supplierOrders.documentTypes.${value}`),
                              },
                              {
                                title: t('orders:supplierOrders.detail.documentNumber'),
                                dataIndex: 'number',
                                key: 'number',
                                render: (value: string | null, record: SupplierOrderDocumentDto) => value || record.fileName,
                              },
                              {
                                title: t('orders:supplierOrders.detail.documentDate'),
                                dataIndex: 'documentDate',
                                key: 'documentDate',
                                render: (value?: string | null) => value ? formatDate(value) : '-',
                              },
                              {
                                title: t('orders:orderDetail.amount'),
                                key: 'amount',
                                align: 'right' as const,
                                render: (_: unknown, record: SupplierOrderDocumentDto) => (
                                  record.amount != null ? formatCurrency(Number(record.amount), record.currency || 'PLN') : '-'
                                ),
                              },
                              {
                                title: t('orders:orderDetail.actions'),
                                key: 'actions',
                                width: 120,
                                render: (_: unknown, record: SupplierOrderDocumentDto) => (
                                  <Space>
                                    <Tooltip title={t('orders:supplierOrders.detail.downloadSupplierDocument')}>
                                      <Button
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownloadSupplierDocument(record)}
                                        loading={supplierDocumentDownloadingId === record.id}
                                      />
                                    </Tooltip>
                                    {canManagePurchaseInvoices && (
                                      <Tooltip title={t('common:delete')}>
                                        <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteSupplierDocument(record)} />
                                      </Tooltip>
                                    )}
                                  </Space>
                                ),
                              },
                            ]}
                            locale={{ emptyText: t('orders:supplierOrders.detail.noSupplierDocuments') }}
                          />
                        )}
                      </OrderDetailSectionCard>
                    </motion.div>

	                </Space>
	              ),
	            },

            // ───────────────── Tab 2: Aktywność ─────────────────
            {
              key: 'activity',
              label: (
                <span>
                  <FileTextOutlined /> {t('orders:orderDetail.activity')}
                </span>
              ),
              children: (
                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
                  <OrderDetailSectionCard
                    title={t('orders:orderDetail.statusHistory')}
                    bodyStyle={{ paddingTop: 16 }}
                  >
                    <Timeline style={{ marginBottom: 0 }}>
                      {statusHistoryEntries.map((history) => (
                        <Timeline.Item key={history.id}>
                          <div>
                            <strong>{t(`orders:supplierOrders.status.${history.status}`)}</strong>
                            {history.changedAt && (
                              <div style={{ fontSize: 12, color: '#999' }}>{formatDateTime(history.changedAt)}</div>
                            )}
                            {history.changedByName && (
                              <div style={{ fontSize: 12, color: '#64748b' }}>
                                {history.changedByName}
                              </div>
                            )}
                            {history.notes && <div style={{ fontSize: 12 }}>{history.notes}</div>}
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </OrderDetailSectionCard>
                </motion.div>
              ),
            },

            // ───────────────── Tab 3: Powiązania ─────────────────
            {
              key: '3',
              label: (
                <span>
                  <LinkOutlined /> {t('orders:supplierOrders.relations')}
                </span>
              ),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {/* Card: Transporty dostawy (Etap 2 — transport przywozowy;
                      2026-08-11 przeniesione tu z zakładki Informacje, bez
                      duplikacji — jedna lokalizacja). "Which trucks are
                      bringing this in" belongs with the other cross-module
                      links, not the editable order fields. Read-only: the
                      links themselves are owned by the transport form. */}
                  <OrderDetailSectionCard
                    title={t('orders:supplierOrders.detail.deliveryTransports')}
                    forceRender
                    data-testid="supplier-order-delivery-transports"
                  >
                    <SupplierOrderDeliveryTransportsSection
                      supplierOrderId={id}
                      onOpenTransport={(transportId) => navigate(`/app/transports/${transportId}`)}
                    />
                  </OrderDetailSectionCard>

                  {/* Card: Dokumenty PZ — the PZ documents already issued off
                      this ZD, read-only (owned by the PZ module). */}
                  <OrderDetailSectionCard
                    title={t('orders:supplierOrders.detail.pzDocuments')}
                    forceRender
                    data-testid="supplier-order-pz-documents"
                  >
                    <SupplierOrderPZDocumentsSection supplierOrderId={id} />
                  </OrderDetailSectionCard>

                </Space>
              ),
            },

            // ───────────────── Tab 4: Finanse ─────────────────
            ...(canViewSupplierOrderFinance ? [{
              key: '4',
              label: (
                <span>
                  <DollarOutlined /> {t('orders:supplierOrders.detail.finances')}
                </span>
              ),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <SupplierOrderPurchaseInvoicesSection key={supplierOrder.id} supplierOrderId={supplierOrder.id}
                    canManage={authService.canManagePurchaseInvoiceImports()} onChanged={refresh} />
                  <SupplierOrderPurchaseCostsSection supplierOrderId={supplierOrder.id}
                    showFreight={canViewSupplierPurchasePrices}
                    landedCostPLN={supplierOrder.landedCostAmountPLN ?? null} revision={supplierOrder}>
                    {canEditSupplierOrderFinance
                      ? renderEditableField('landedCosts', landedCostsDisplay, landedCostsForm)
                      : landedCostsDisplay}
                  </SupplierOrderPurchaseCostsSection>
                  {canViewSupplierPurchasePrices && (
                    <OrderDetailSectionCard
                      title={t('orders:supplierOrders.finance.plannedTitle')}
                      bodyStyle={{ paddingTop: 16 }}
                    >
                      <Typography.Paragraph type="secondary">{t('orders:supplierOrders.finance.plannedHint')}</Typography.Paragraph>
                      <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                        <Descriptions.Item label={t('orders:supplierOrders.detail.purchaseCostSummaryCurrency')}>
                          {supplierOrder.totalAmount == null ? '—' : formatCurrency(supplierOrder.totalAmount, supplierOrder.currency)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('orders:supplierOrders.detail.purchaseCostConvertedPLN')}>
                          {supplierOrder.totalAmountPLN == null ? '—' : formatCurrency(supplierOrder.totalAmountPLN, 'PLN')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('orders:supplierOrders.detail.purchaseLineCurrencies')}>
                          {supplierOrderLineCurrenciesLabel}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('orders:supplierOrders.detail.headerExchangeRate')}>
                          {renderExchangeRateToPLN()}
                        </Descriptions.Item>
                      </Descriptions>
                    </OrderDetailSectionCard>
                  )}

                </Space>
              ),
            }] : []),

            // ───────────────── Tab 5: Zadania ─────────────────
            // Mirrors the customer Tasks tab: same centered empty-state copy
            // when there are no tasks. When data exists, the 5 columns mirror
            // customer's: taskTitle / status (Tag) / priority (Tag) /
            // assignee / dueDate (Tag with calendar icon).
            {
              key: '5',
              label: (
                <span>
                  <CheckSquareOutlined /> {t('tasks:orderDetailTab')}
                </span>
              ),
              children: (
                <OrderDetailSectionCard title={t('tasks:orderDetailTab')}>
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                    {t('tasks:noTasks')}
                  </div>
                </OrderDetailSectionCard>
              ),
            },
          ]}
        />
      </div>

      {/* Supplier purchase document modal */}
      <Modal
        title={t('orders:supplierOrders.detail.addSupplierDocument')}
        open={supplierDocumentModalVisible}
        onCancel={() => setSupplierDocumentModalVisible(false)}
        onOk={handleCreateSupplierDocument}
        confirmLoading={supplierDocumentSubmitting}
        maskClosable={false}
      >
        <Form form={supplierDocumentForm} layout="vertical">
          <Form.Item name="documentType" label={t('orders:supplierOrders.detail.documentType')} rules={[{ required: true }]}>
            <Select>
              {['SUPPLIER_INVOICE', 'TRANSPORT_INVOICE', 'CUSTOMS_DOCUMENT', 'OTHER'].map((type) => (
                <Select.Option key={type} value={type}>
                  {t(`orders:supplierOrders.documentTypes.${type}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="number" label={t('orders:supplierOrders.detail.documentNumber')}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={12}>
              <Form.Item name="documentDate" label={t('orders:supplierOrders.detail.documentDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="currency" label={t('orders:currency')} rules={[{ required: true }]}>
                <Select>
                  {ORDER_CURRENCIES.map((currency) => (
                    <Select.Option key={currency} value={currency}>{currency}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="amount"
            label={t('orders:orderDetail.amount')}
            rules={[supplierOrderAmountRule(t)]}
          >
            <InputNumber
              precision={2}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="document"
            label={t('orders:supplierOrders.detail.documentFile')}
            valuePropName="fileList"
            getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}
            rules={[{ required: true, message: t('orders:supplierOrders.detail.documentFileRequired') }]}
          >
            <Upload
              accept=".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx"
              beforeUpload={() => false}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>{t('orders:supplierOrders.detail.selectDocumentFile')}</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Status modal */}
      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleChangeStatus}>
          <Form.Item name="status" label={t('orders:supplierOrders.form.status')} rules={[{ required: true }]}>
            <OrderStatusTransitionSelect
              statuses={SUPPLIER_ORDER_STATUSES}
              currentStatus={supplierOrder.status}
              allowedTransitions={allowedStatusTransitions}
              getStatusLabel={(status) => t(`orders:supplierOrders.status.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
            />
          </Form.Item>
          <Form.Item name="statusNotes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={statusSaving}>
                {t('common:save')}
              </Button>
              <Button onClick={() => setStatusModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default SupplierOrderDetail;
